import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/sections/Footer';
import { useCart } from '@/contexts/CartContext';
import { SEO } from '@/components/SEO';
import { SkipLink } from '@/components/SkipLink';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { HtmlContent } from '@/components/ui/html-content';
import { stripHtml } from '@/lib/html-utils';
import { Plus, Minus, ShoppingCart, Loader2, ArrowLeft } from 'lucide-react';
import { fetchProduct, mapWooCommerceProduct } from '@/lib/woocommerce';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { trackViewContent } from '@/lib/meta-pixel';

const Prodotto = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);

  const { data: wcProduct, isLoading, error } = useQuery({
    queryKey: ['woocommerce-product', id],
    queryFn: () => fetchProduct(Number(id)),
    enabled: !!id,
  });

  const product = wcProduct ? mapWooCommerceProduct(wcProduct) : null;

  const handleAddToCart = () => {
    if (!product) return;
    addToCart(product, quantity);
    toast.success(`${product.name} aggiunto al carrello`, {
      description: `Quantità: ${quantity}`,
    });
    setQuantity(1);
  };

  const handleQuantityChange = (delta: number) => {
    setQuantity(prev => Math.max(1, prev + delta));
  };

  if (isLoading) {
    return (
      <>
        <SkipLink />
        <main id="main-content" className="min-h-screen bg-background">
          <SEO title="Caricamento prodotto..." />
          <Header />
          <div className="pt-24 pb-16 px-4 md:px-6">
            <div className="mx-auto max-w-7xl">
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2 text-muted-foreground">Caricamento prodotto...</span>
              </div>
            </div>
          </div>
          <Footer />
        </main>
      </>
    );
  }

  if (error || !product) {
    return (
      <>
        <SkipLink />
        <main id="main-content" className="min-h-screen bg-background">
          <SEO title="Prodotto non trovato" />
          <Header />
          <div className="pt-24 pb-16 px-4 md:px-6">
            <div className="mx-auto max-w-7xl">
              <div className="py-16 text-center">
                <p className="mb-4 text-destructive">Prodotto non trovato</p>
                <Button onClick={() => navigate('/catalogo')}>
                  Torna al Catalogo
                </Button>
              </div>
            </div>
          </div>
          <Footer />
        </main>
      </>
    );
  }

  // Prepara dati SEO ottimizzati
  const productDescription = product.description 
    ? stripHtml(product.description).substring(0, 160) 
    : `Acquista ${product.name} da Brace e Sapori Store. Prodotti tipici pugliesi di alta qualità, spedizione in tutta Italia.`;
  
  const productImage = product.image || '/logo.png';
  const productPrice = product.price.toFixed(2);
  const productAvailability = product.inStock ? 'in_stock' : 'out_of_stock';
  const productCategory = product.category ? stripHtml(product.category) : 'Prodotti Tipici Pugliesi';
  
  // Structured Data per prodotto
  const productStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: stripHtml(product.description || ''),
    image: productImage,
    brand: {
      '@type': 'Brand',
      name: 'Brace e Sapori',
    },
    offers: {
      '@type': 'Offer',
      url: `${window.location.origin}/prodotto/${id}`,
      priceCurrency: 'EUR',
      price: productPrice,
      availability: `https://schema.org/${product.inStock ? 'InStock' : 'OutOfStock'}`,
      seller: {
        '@type': 'Organization',
        name: 'Brace e Sapori Store',
      },
    },
    category: productCategory,
    sku: wcProduct?.sku || `PROD-${id}`,
    ...(wcProduct?.weight && { weight: `${wcProduct.weight} kg` }),
  };

  // Breadcrumbs structured data
  const breadcrumbsStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${window.location.origin}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Catalogo',
        item: `${window.location.origin}/catalogo`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: product.name,
        item: `${window.location.origin}/prodotto/${id}`,
      },
    ],
  };

  return (
    <>
      <SkipLink />
      <main id="main-content" className="min-h-screen bg-background">
        <SEO
          title={`${product.name} - Brace e Sapori Store`}
          description={productDescription}
          keywords={`${product.name}, ${productCategory}, prodotti pugliesi, brace e sapori, costate online`}
          ogImage={productImage}
          ogType="product"
          productPrice={productPrice}
          productCurrency="EUR"
          productAvailability={productAvailability}
          structuredData={[productStructuredData, breadcrumbsStructuredData]}
        />
        <Header />
        
        <div className="pt-24 pb-16 px-4 md:px-6">
          <div className="mx-auto max-w-7xl">
            <Button
              variant="ghost"
              onClick={() => navigate('/catalogo')}
              className="mb-6"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Torna al Catalogo
            </Button>

            <div className="grid gap-8 lg:grid-cols-2">
              {/* Immagine */}
              <div className="aspect-square overflow-hidden rounded-lg bg-muted">
                <img
                  src={product.image}
                  alt={product.name}
                  className="h-full w-full object-cover"
                />
              </div>

              {/* Dettagli */}
              <div className="space-y-6">
                <div>
                  {product.category && (
                    <Badge className="mb-2" variant="secondary">
                      <HtmlContent html={product.category} />
                    </Badge>
                  )}
                  <h1 className="mb-4 text-4xl font-bold">{product.name}</h1>
                  {product.origin && (
                    <p className="text-sm text-primary">{product.origin}</p>
                  )}
                </div>

                <Separator />

                <div>
                  <div className="mb-4 flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-primary">
                      €{product.price.toFixed(2)}
                    </span>
                    {product.weight && (
                      <span className="text-muted-foreground">
                        / {product.weight}
                      </span>
                    )}
                  </div>
                  {wcProduct?.on_sale && wcProduct.regular_price && (
                    <p className="text-sm text-muted-foreground line-through">
                      €{parseFloat(wcProduct.regular_price).toFixed(2)}
                    </p>
                  )}
                </div>

                {product.description && (
                  <div>
                    <h2 className="mb-2 text-xl font-semibold">Descrizione</h2>
                    <HtmlContent
                      html={product.description}
                      className="text-muted-foreground prose prose-sm max-w-none prose-headings:font-semibold prose-p:mb-2 prose-ul:list-disc prose-ol:list-decimal"
                    />
                  </div>
                )}

                <Separator />

                {/* Quantità e Aggiungi al Carrello */}
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium">Quantità:</span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-10 w-10"
                        onClick={() => handleQuantityChange(-1)}
                        disabled={quantity <= 1}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-12 text-center text-lg font-medium">
                        {quantity}
                      </span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-10 w-10"
                        onClick={() => handleQuantityChange(1)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <Button
                    className="w-full"
                    size="lg"
                    onClick={handleAddToCart}
                    disabled={!product.inStock}
                  >
                    <ShoppingCart className="mr-2 h-5 w-5" />
                    {product.inStock ? 'Aggiungi al Carrello' : 'Non disponibile'}
                  </Button>

                  {!product.inStock && (
                    <p className="text-sm text-destructive">
                      Prodotto momentaneamente esaurito
                    </p>
                  )}
                </div>

                {/* Informazioni aggiuntive */}
                {wcProduct && (
                  <div className="space-y-2 text-sm text-muted-foreground">
                    {wcProduct.sku && (
                      <p>
                        <span className="font-medium">SKU:</span> {wcProduct.sku}
                      </p>
                    )}
                    {wcProduct.weight && (
                      <p>
                        <span className="font-medium">Peso:</span> {wcProduct.weight} kg
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </main>
    </>
  );
};

export default Prodotto;

